import EmptyState from '@/components/EmptyState';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useFacebookToken } from '@/hooks/useFacebookToken';
import { motion } from 'framer-motion';
import { Users2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LoadingScreen } from './LoadingScreen';

type FacebookAccount = {
  id: string;
  instagram_business_account: {
    id: string;
  };
  insights?: {
    id: string;
    biography: string;
    followers_count: number;
    follows_count: number;
    profile_picture_url: string;
    username: string;
    website: string;
  };
};

const FacebookAccountSelect = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<string | null>(null);
  const {
    data: facebookAccounts,
    isLoading: facebookAccountsLoading,
    connectAccount,
  } = useFacebookToken();

  const handleConnect = async (accountId: string) => {
    setLoading(accountId);
    await connectAccount.mutateAsync(accountId);
    setLoading(null);
    toast.success('Facebook account connected');

    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  if (facebookAccountsLoading) {
    return <LoadingScreen message="Loading Instagram accounts..." />;
  }

  if (facebookAccounts?.length === 0) {
    return (
      <EmptyState
        title="No Instagram accounts found"
        description="Connect your Instagram account to get started"
        actionText="Connect Instagram account"
        onActionClick={() => {
          navigate('/');
        }}
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Select Instagram Account</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-4">
          {facebookAccounts?.map((account: FacebookAccount) => (
            <Card key={account.id} className="p-4">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}>
                    <Avatar className="h-12 w-12">
                      <AvatarImage
                        src={account.insights?.profile_picture_url}
                        alt={account.insights?.username}
                      />
                      <AvatarFallback>
                        {account.insights?.username}
                      </AvatarFallback>
                    </Avatar>
                  </motion.div>
                  <CardTitle>
                    <motion.div
                      className="flex flex-col gap-2"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 }}>
                      <div className="flex items-start flex-col">
                        <div className="text-sm font-medium">
                          {account.insights?.username}
                        </div>
                        <div className="text-xs font-light text-gray-500">
                          {account.insights?.biography}
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1">
                          <Users2 className="w-4 h-4 text-gray-500" />
                          <div className="text-sm text-gray-500">
                            {Intl.NumberFormat('en-US', {
                              notation: 'compact',
                            }).format(
                              Number(account.insights?.followers_count),
                            )}{' '}
                            followers
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </CardTitle>
                </div>
                <Button
                  variant="outline"
                  onClick={() =>
                    handleConnect(account.instagram_business_account?.id)
                  }
                  isLoading={
                    connectAccount.isPending &&
                    loading === account.instagram_business_account?.id
                  }>
                  Connect
                </Button>
              </CardTitle>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default FacebookAccountSelect;
